import { useState, useRef, useEffect, useContext, useCallback } from "react";
import { createPortal } from "react-dom";
import styles from "./teamCollaboration.module.css";
import { ChevronDown, LogOut, Plus, Trash2 } from "lucide-react";
import { PentestContext } from "../../../../contexts/PentestContext";
import InfoHover from "../../../Common/Tooltips/InformationHover/InformationHover";
import { AppContext } from "../../../../contexts/AppContext";
import CopyBlock from "../../../Common/CopyBlock/CopyBlock";
import { AuthContext } from "../../../../contexts/AuthContext";
import {
  apiCreateJoinToken,
  apiDeleteCollaborator,
  apiGetCollaborators,
  apiLeavePentest,
  apiUpdateCollaborator,
} from "../../../../sdk";
import { RoleType, TeamMember } from "../../../../sdkStructs";
import { useAuth0 } from "@auth0/auth0-react";
import Button from "../../../Common/Button/Button";
import { useNavigate } from "react-router-dom";
import axios, { isAxiosError } from "axios";
import { useLogError } from "../../../../hooks/useAxiosInterceptor";

// Helper function to generate initials from email
const getInitialsFromEmail = (email: string): string => {
  if (!email || email.length === 0) return "??";

  // Take first two characters of the email and convert to uppercase
  return email.substring(0, 2).toUpperCase();
};

export default function TeamManagement() {
  const navigate = useNavigate();
  const logError = useLogError();

  const { token } = useContext(AuthContext);
  const { currentWindow, pentestId } = useContext(PentestContext);
  const { setShowModal, setLoading, pushAlert } = useContext(AppContext);

  const [members, setMembers] = useState<TeamMember[]>([]);
  const { user } = useAuth0();

  // Track which dropdown is currently open
  const [openRoleDropdownId, setOpenRoleDropdownId] = useState<number | null>(null);

  // References for dropdown positioning
  const roleButtonRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // State for dropdown positions
  const [roleDropdownPosition, setRoleDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      // Close role dropdown if clicking outside
      if (openRoleDropdownId) {
        const roleButton = roleButtonRefs.current[openRoleDropdownId];
        if (roleButton && !roleButton.contains(event.target as Node)) {
          const dropdownElement = document.getElementById(`role-dropdown-${openRoleDropdownId}`);
          if (!dropdownElement || !dropdownElement.contains(event.target as Node)) {
            setOpenRoleDropdownId(null);
          }
        }
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openRoleDropdownId]);

  const fetchMembers = useCallback(async () => {
    const response = await apiGetCollaborators(token, pentestId);
    setMembers(response.data);
  }, [pentestId, token]);

  async function fetchShareToken(role: RoleType) {
    const response = await apiCreateJoinToken(token, pentestId, role);
    return response.data.token;
  }

  useEffect(() => {
    if (token) {
      fetchMembers();
    }
  }, [token, fetchMembers]);

  // Toggle role dropdown visibility
  const toggleRoleDropdown = (id: number) => {
    if (openRoleDropdownId === id) {
      setOpenRoleDropdownId(null);
    } else {
      const buttonElement = roleButtonRefs.current[id];
      if (buttonElement) {
        const rect = buttonElement.getBoundingClientRect();
        setRoleDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
        });
        setOpenRoleDropdownId(id);
      }
    }
  };

  // Change a member's role
  const changeRole = async (id: number, newRole: RoleType) => {
    //setMembers(members.map((member) => (member.user_id === id ? { ...member, role: newRole } : member)));
    try {
      setOpenRoleDropdownId(null);
      await apiUpdateCollaborator(token, pentestId, id, newRole);
      fetchMembers();
    } catch(e){
      if(!isAxiosError(e))
        return;
      logError(e);
    }
  };

  // Remove a team member
  const removeTeamMember = async (id: number) => {
    //setMembers(members.filter((member) => member.user_id !== id));
    await apiDeleteCollaborator(token, pentestId, id);
    fetchMembers();
  };
  const confirmRemoveTeamMember = (id: number) => {
    setShowModal("confirm", {
      text: "Are you sure you want to remove this user from the team?",
      confirm: () => removeTeamMember(id),
      close: () => setShowModal("none", undefined),
    });
  };

  const leavePentest = async () => {
    setLoading(true);
    try {
      await apiLeavePentest(token, pentestId);
      navigate("/");
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        return;
      }
      if (
        error.response?.data.error ===
        "Cannot leave the pentest as you are the last owner. Promote another collaborator to owner first."
      )
        pushAlert({
          title: "Bad Request",
          text: "The last owner in a team cannot leave the pentest! Promote another collaborator first, or delete the pentest entirely.",
          closeButton: true,
          type: "error",
          time: 8000,
        });
    }
    setLoading(false);
  };
  const confirmLeavePentest = () => {
    setShowModal("confirm", {
      text: "Are you sure you want to leave this pentest?",
      confirm: () => leavePentest(),
      close: () => setShowModal("none", undefined),
    });
  };

  // Add a new team member
  //const addTeamMember = () => {
  //  const newId = `member-${Date.now()}`;
  //
  //  const newMember: TeamMember = {
  //    id: newId,
  //    name: "New Team Member",
  //    email: "new.member@example.com",
  //    role: "Viewer", // Default role
  //  };
  //
  //  setMembers([...members, newMember]);
  //};

  const inviteTeamMember = async (selectedRole: RoleType) => {
    setLoading(true);
    try {
      const shareToken = await fetchShareToken(selectedRole);

      setShowModal("generic", {
        content: (
          <div className={styles.shareContent}>
            <h3 className={styles.shareTitle}>Invite People</h3>
            <div className={styles.roleSelectorContainer}>
              <label htmlFor="role-select" className={styles.roleSelectorLabel}>
                Permissions:
              </label>
              <select
                id="role-select"
                className={styles.roleSelect}
                value={selectedRole}
                onChange={(e) => {
                  setShowModal("none", undefined);
                  inviteTeamMember(e.target.value as RoleType);
                }}
              >
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
                <option value="owner">Owner</option>
              </select>

              <div className={styles.roleDescription}>
                {selectedRole === "viewer" && "Can view but not edit the pentest"}
                {selectedRole === "editor" && "Can view and edit the pentest"}
                {selectedRole === "owner" && "Full access including managing team members"}
              </div>
            </div>

            <div className={styles.shareText}>Send this token to you teammates to collaborate on this pentest:</div>

            <CopyBlock text={shareToken} />
          </div>
        ),
        close: () => setShowModal("none", undefined),
      });
    } catch (error) {
      console.log("ERROR GENERATING INVITE TOKEN:", error);
    }
    setLoading(false);
  };

  // Render role dropdown portal
  const renderRoleDropdown = () => {
    if (!openRoleDropdownId) return null;

    return createPortal(
      <div
        id={`role-dropdown-${openRoleDropdownId}`}
        className={styles.dropdownMenuPortal}
        style={{
          position: "absolute",
          top: `${roleDropdownPosition.top}px`,
          left: `${roleDropdownPosition.left}px`,
          width: `${roleDropdownPosition.width}px`,
        }}
      >
        <button
          className={styles.dropdownItem + " " + styles.dropdownItemBorder}
          onClick={() => changeRole(openRoleDropdownId, "owner")}
        >
          Owner
        </button>
        <button
          className={styles.dropdownItem + " " + styles.dropdownItemBorder}
          onClick={() => changeRole(openRoleDropdownId, "editor")}
        >
          Editor
        </button>
        <button className={styles.dropdownItem} onClick={() => changeRole(openRoleDropdownId, "viewer")}>
          Viewer
        </button>
      </div>,
      document.body,
    );
  };

  if (currentWindow !== "collaboration") return;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Manage Access</h1>
        <InfoHover description="The people who have access to this pentest. Only owners can add new members to the team." />
        <div className={styles.buttonContainer}>
          <Button variant="primary" textcolor="light" onClick={() => inviteTeamMember("viewer")}>
            <Plus strokeWidth="1.5" className={styles.iconMargin} />
            Invite People
          </Button>
          <Button variant="primary" color="red" textcolor="light" onClick={confirmLeavePentest}>
            <LogOut strokeWidth="1.5" className={styles.iconMargin} />
            <span>Leave Pentest</span>
          </Button>
        </div>
      </div>

      <div className={styles.tableContainer}>
        <div className={styles.tableHeader}>
          <div className={styles.membersColumn}>Members</div>
          <div className={styles.roleColumn}>Team Role</div>
          <div className={styles.roleColumn}>Options</div>
        </div>

        <div className={styles.tableBody}>
          {members.map((member) => (
            <div
              key={member.user_id}
              className={styles.tableRow + " " + (member.email === user?.email ? styles.userTableRow : "")}
            >
              <div className={styles.memberInfo}>
                <div className={styles.avatar}>
                  {member.avatarUrl ? (
                    <img
                      src={member.avatarUrl || "/placeholder.svg"}
                      alt={`${member.name}'s avatar`}
                      className={styles.avatarImage}
                    />
                  ) : (
                    <div className={styles.avatarInitials}>{getInitialsFromEmail(member.email)}</div>
                  )}
                </div>
                <div className={styles.userDetails}>
                  <div className={styles.userName}>{member.name}</div>
                  {member.name !== member.email && <div className={styles.userEmail}>{member.email}</div>}
                </div>
              </div>

              <div className={styles.roleSelector}>
                <div
                  className={styles.roleDropdown}
                  onClick={() => toggleRoleDropdown(member.user_id)}
                  ref={(el) => (roleButtonRefs.current[member.user_id] = el)}
                >
                  <span>{member.role.charAt(0).toUpperCase() + member.role.slice(1)}</span>
                  <ChevronDown size={16} className={styles.dropdownIcon} />
                </div>
              </div>

              {member.email !== user?.email && (
                <div className={styles.options}>
                  <button
                    className={styles.optionsButton}
                    onClick={() => confirmRemoveTeamMember(member.user_id)}
                    title="Remove teammate"
                  >
                    <Trash2 stroke="var(--red-button)" size={18} className={styles.optionsIcon} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {renderRoleDropdown()}
    </div>
  );
}
